import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfig } from '@env/app-config';
import {
  AgentDownload, InvoiceTicket, PagedPrintJobs, PrintJob, PrintStation, PrintStationRequest, PrintTicket,
} from './printing.model';

/** API de impresión de comprobantes en ticketera (Apps → Facturación → Impresora de tickets). */
@Injectable({ providedIn: 'root' })
export class PrintingService {
  private readonly http = inject(HttpClient);
  private readonly base = `${AppConfig.apiUrl}/api/shop/printing`;

  private params(companyId: number): HttpParams {
    return new HttpParams().set('companyId', String(companyId));
  }

  /** Enlace firmado y temporal al instalador del micro «ticketera» (versión vigente). */
  agentDownload(companyId: number, os: 'windows' | 'linux'): Observable<AgentDownload> {
    return this.http.get<AgentDownload>(`${this.base}/agent/download`, { params: this.params(companyId).set('so', os) });
  }

  listStations(companyId: number): Observable<PrintStation[]> {
    return this.http.get<PrintStation[]>(`${this.base}/stations`, { params: this.params(companyId) });
  }

  createStation(companyId: number, body: PrintStationRequest): Observable<PrintStation> {
    return this.http.post<PrintStation>(`${this.base}/stations`, body, { params: this.params(companyId) });
  }

  updateStation(companyId: number, stationId: number, body: PrintStationRequest): Observable<PrintStation> {
    return this.http.put<PrintStation>(`${this.base}/stations/${stationId}`, body, { params: this.params(companyId) });
  }

  deleteStation(companyId: number, stationId: number): Observable<void> {
    return this.http.delete<void>(`${this.base}/stations/${stationId}`, { params: this.params(companyId) });
  }

  heartbeat(companyId: number, stationId: number, agentPort: number | null): Observable<PrintStation> {
    return this.http.post<PrintStation>(`${this.base}/stations/${stationId}/heartbeat`, { agentPort },
      { params: this.params(companyId) });
  }

  testTicket(companyId: number, stationId: number): Observable<PrintTicket> {
    return this.http.post<PrintTicket>(`${this.base}/stations/${stationId}/test-ticket`, null, { params: this.params(companyId) });
  }

  claim(companyId: number, stationId: number): Observable<PrintTicket[]> {
    return this.http.post<PrintTicket[]>(`${this.base}/stations/${stationId}/claim`, null, { params: this.params(companyId) });
  }

  markPrinted(companyId: number, jobId: number, stationId: number): Observable<void> {
    return this.http.post<void>(`${this.base}/jobs/${jobId}/printed`, { stationId }, { params: this.params(companyId) });
  }

  markFailed(companyId: number, jobId: number, stationId: number, error: string): Observable<void> {
    return this.http.post<void>(`${this.base}/jobs/${jobId}/failed`, { stationId, error: error.slice(0, 500) },
      { params: this.params(companyId) });
  }

  retryJob(companyId: number, jobId: number): Observable<PrintJob> {
    return this.http.post<PrintJob>(`${this.base}/jobs/${jobId}/retry`, null, { params: this.params(companyId) });
  }

  listJobs(companyId: number, status: string | null, page = 0, size = 20): Observable<PagedPrintJobs> {
    let params = this.params(companyId).set('page', String(page)).set('size', String(size));
    if (status) params = params.set('status', status);
    return this.http.get<PagedPrintJobs>(`${this.base}/jobs`, { params });
  }

  /** Comandos del ticket de un comprobante para imprimirlo a mano en el micro de esta PC. */
  invoiceTicket(companyId: number, invoiceId: number): Observable<InvoiceTicket> {
    return this.http.get<InvoiceTicket>(`${this.base}/invoices/${invoiceId}/ticket`, { params: this.params(companyId) });
  }

  /** Registra (auditoría) una impresión manual que el micro confirmó. */
  invoicePrinted(companyId: number, invoiceId: number, stationId: number | null): Observable<PrintJob> {
    let params = this.params(companyId);
    if (stationId) params = params.set('stationId', String(stationId));
    return this.http.post<PrintJob>(`${this.base}/invoices/${invoiceId}/printed`, null, { params });
  }
}
